import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Users, Search, ShieldAlert, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { GovernanceService } from '../services/GovernanceService';
import { UserService } from '../services/api';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { getRankRingParams, getRankBadge } from './utils/rankUtils';
import { FlagImage } from './utils/countryFlags';
import { UserDetailModal } from './UserDetailModal';
import RankBadge from './RankBadge';
import { getUserAvatar } from '../utils/userUtils';

interface GroupMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  type: 'forum' | 'tribe';
  hiddenOwner?: boolean;
}

interface GroupMembersModalContentProps extends GroupMembersModalProps {
  onUserSelect: (user: any) => void;
}

function GroupMembersModalContent({ isOpen, onClose, groupId, type, hiddenOwner, onUserSelect }: GroupMembersModalContentProps) {
  const { isFriend } = useUserData();
  const { isBlockedId } = useContentFilter();
  const [members, setMembers] = useState<any[]>([]);
  const [fetchingMembers, setFetchingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const MEMBERS_PER_PAGE = 20;

  useEffect(() => {
    if (isOpen && groupId) {
      setMemberSearch('');
      setMemberPage(1);
      setMembers([]);
      loadMembers();
    }
  }, [isOpen, groupId]);

  const loadMembers = async () => {
    setFetchingMembers(true);
    try {
      const memberRecords = await GovernanceService.getGroupMembers(type, groupId);
      const enrichedMembers = await Promise.all(memberRecords.map(async (m: any) => {
        try {
          const user = await UserService.fetchUserById(m.userId || m.id);
          return { ...m, ...user };
        } catch {
          return m;
        }
      }));

      // 1. Filter out accounts that are not fully set up, and hide owner for hidden-owner university tribes
      const validMembers = enrichedMembers.filter(m => {
        if (hiddenOwner && (m.role || '').toLowerCase() === 'owner') return false;
        return m.name && m.name.trim() !== '' && m.name.toLowerCase() !== 'member' &&
          m.username && m.username.trim() !== '' && m.username.toLowerCase() !== 'unknown';
      });

      // 2. Sort by role: owner first, then watchers, then members
      const roleOrder: Record<string, number> = {
        'owner': 0,
        'watcher': 1,
        'member': 2
      };

      validMembers.sort((a, b) => {
        const roleA = (a.role || 'member').toLowerCase();
        const roleB = (b.role || 'member').toLowerCase();
        const rankA = roleOrder[roleA] ?? 99;
        const rankB = roleOrder[roleB] ?? 99;
        
        if (rankA !== rankB) return rankA - rankB;
        // Alphabetical fallback for same role
        return (a.name || '').localeCompare(b.name || '');
      });

      setMembers(validMembers);
    } catch (error) {
      console.error("Failed to load members:", error);
      toast.error("Failed to load members");
    } finally {
      setFetchingMembers(false);
    }
  };

  const handleViewProfile = (member: any) => {
    onUserSelect(member);
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-sm bg-light-pattern dark:bg-[#1a1a1a] rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
          >
            <div className="p-4 border-b border-black/5 dark:border-white/5 bg-light-pattern dark:bg-[#1a1a1a] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#4478ea]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#4478ea]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-black dark:text-white">Members</h2>
                  <p className="text-[10px] text-black/60 dark:text-white/60">{members.length} member{members.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-black dark:text-white" />
              </motion.button>
            </div>

            <div className="p-4 shrink-0 border-b border-black/5 dark:border-white/5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/30 dark:text-white/30" />
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => { setMemberSearch(e.target.value); setMemberPage(1); }}
                  placeholder="Search members..."
                  className="w-full pl-9 pr-3 h-10 text-sm rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 outline-none focus:ring-1 focus:ring-[#4478ea]/40"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 custom-scrollbar">
              {fetchingMembers ? (
                <div className="flex flex-col items-center justify-center py-10 text-black/40 dark:text-white/40">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                    <Users className="w-8 h-8" />
                  </motion.div>
                  <p className="text-xs font-bold mt-2">Loading members...</p>
                </div>
              ) : (() => {
                const filtered = members.filter(m => {
                  // Blocked either way: never list them among the members.
                  if (isBlockedId(m?.id) || isBlockedId(m?.userId)) return false;
                  if (!memberSearch.trim()) return true;
                  const q = memberSearch.toLowerCase();
                  return (
                    (m.name || '').toLowerCase().includes(q) ||
                    (m.username || '').toLowerCase().replace('@', '').includes(q.replace('@', ''))
                  );
                });
                const paginated = filtered.slice(0, memberPage * MEMBERS_PER_PAGE);
                const hasMore = paginated.length < filtered.length;

                if (paginated.length === 0) {
                  return (
                    <div className="text-center py-10 text-black/40 dark:text-white/40">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-20" />
                      <p className="text-sm">No members found</p>
                    </div>
                  );
                }

                return (
                  <>
                    {paginated.map((member) => (
                      <div 
                        key={member.id} 
                        className="flex items-center justify-between p-2 rounded-2xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => handleViewProfile(member)}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={getUserAvatar(member)}
                            className={`w-10 h-10 rounded-full border border-black/10 ${member.rank ? getRankRingParams(member.rank).ringClass : ''}`}
                            alt={member.name}
                          />
                          <div>
                            <div className="flex items-center gap-1.5">
                                <p className="text-sm font-bold text-black dark:text-white flex items-center gap-1.5">
                                  {member.name || 'Member'}
                                  {(member.countryFlag || member.country) && (
                                    <FlagImage 
                                      countryName={member.countryFlag || member.country} 
                                      size="w80" 
                                      className="w-4 aspect-3/2 object-contain rounded" 
                                    />
                                  )}
                                </p>
                                <RankBadge rank={member.rank} size={16} />
                            </div>
                            <div className="flex items-center gap-1">
                              <p className="text-[10px] text-black/60 dark:text-white/60">@{member.username ? member.username.replace(/^@/, '') : 'unknown'}</p>
                              {isFriend(String(member.userId || member.id)) && (
                                <UserCheck className="w-3 h-3 text-[#4478ea]" />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                           <span className="text-[10px] font-bold px-2 py-1 rounded-md bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60 capitalize">
                             {member.role || 'Member'}
                           </span>
                        </div>
                      </div>
                    ))}
                    {hasMore && (
                      <button
                        onClick={() => setMemberPage(p => p + 1)}
                        className="w-full py-2 mt-4 text-xs font-bold text-[#4478ea] hover:text-[#4478ea]/80 border border-[#4478ea]/20 hover:border-[#4478ea]/40 rounded-xl transition-colors"
                      >
                        Load More
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function GroupMembersModal(props: GroupMembersModalProps) {
  const [selectedUser, setSelectedUser] = useState<any | null>(null);

  return (
    <>
      <GroupMembersModalContent {...props} onUserSelect={setSelectedUser} />
      {selectedUser && (
        <UserDetailModal
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          user={selectedUser}
        />
      )}
    </>
  );
}
